import { useState } from 'react';
import { DeleteLeaderboardPresets } from '@/connection/leaderboard';
import { updateProjectSettings } from '@/store/slices/project';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { EventSettings } from '@/types/projects.types';
import toastTrigger from '@ui/toast/toast-trigger';
import { ICONS } from '@ui/icons/types';
import Icon from '@ui/icons/icon';
import Modal from '@ui/modal/modal';
import LeaderboardCsvModal from './leaderboard-csv-modal';
import { getUser } from '@/store/slices/user/selectors';
import { getWorkingProject } from '@/store/slices/project/selectors';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import bytesToString from '@/utils/bytes-to-string';
import Popover from '@ui/popover/popover';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import { TButtonAndLinkDesigns } from '@styles/design-system/local-components/local-components.types';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import { EToastTypes } from '@ui/toast/toast.types';

const CsvIcon = () => (
  <Container
    className="flex flex-column gap-1 ai-center jc-center"
    borderRadius="6px"
    background={'#006334'}
    width="29px"
    minWidth="29px"
    height="32px"
  >
    <Icon name={ICONS.VIEWERS_EYE_SOLID} size={12} color="#FFF" />
    <Text fontSize={'7px'} className="font-weight-600">
      CSV
    </Text>
  </Container>
);

const LeaderboardCSVUpload = () => {
  const workingEvent = useAppSelector(getWorkingProject);
  const user = useAppSelector(getUser);

  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();

  const leaderboardSettings = workingEvent?.settings.leaderboard_settings;

  const openModal = () => {
    setCsvModalOpen(true);
  };

  const closeModal = () => {
    setCsvModalOpen(false);
  };

  const handleSubmit = () => {
    toastTrigger('Your upload is processing', {
      description: 'We will notify you when your upload has finished processing',
      type: EToastTypes.info,
    });

    setCsvModalOpen(false);
    return;
  };

  const closeDeleteConfirmation = () => {
    setDeleteConfirmation(false);
    setLoading(false);
  };

  const handleDeleteCsv = async () => {
    if (user?.active_channel && workingEvent) {
      try {
        setLoading(true);
        // delete all rows for uploaded presets on this event
        await DeleteLeaderboardPresets(user.active_channel, workingEvent?.event);

        // remove csv filename + url from event settings and save updates
        const eventSettings: EventSettings = {
          ...workingEvent.settings,
        };

        if (!eventSettings?.leaderboard_settings) return;

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { upload_url, upload_filename, ...rest } = eventSettings.leaderboard_settings;
        const updatedSettings = { ...eventSettings, leaderboard_settings: rest };

        dispatch(updateProjectSettings({ event_uuid: workingEvent.uuid, settings: updatedSettings }));
      } catch (e) {
        console.error(e);
        toastTrigger('Error', {
          description: 'There was an error deleting the uploaded csv.',
          type: EToastTypes.error,
        });
      } finally {
        setLoading(false);
        closeDeleteConfirmation();
      }
    }
  };

  if (!leaderboardSettings) return null;

  return (
    <Container borderRadius="12px" className="flex flex-column gap-12">
      <Container className="flex jc-space-between">
        <div className="flex ai-center gap-8">
          <Text className="H6">CSV List</Text>
          <TooltipPortal
            tooltipContent={
              <Text>
                Assign custom points to attendee by email for actions not captured automatically listed above.
                <br />
                All points are synced to the most recent version of the document.
                <br />
                Uploading a new version will overwrite any existing points assigned via CSV.
              </Text>
            }
          >
            <Icon name={ICONS.INFO_CIRCLE} size={12} color="var(--primitives-mode-1-gray-500)" />
          </TooltipPortal>
        </div>
      </Container>

      <Container>
        {!leaderboardSettings?.upload_filename ? (
          <Container className="flex ai-center jc-center flex-column gap-8" height="120px">
            <Text className="H5">Upload points</Text>
            <Button
              designSize="medium"
              className="margin-t-16"
              backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
              onClick={openModal}
            >
              <Text
                className="L1"
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              >
                Add list
              </Text>
            </Button>
          </Container>
        ) : (
          <Container
            className="flex jc-space-between ai-center"
            gap="8px"
            design="GlobalCont2"
            borderRadius="12px"
            padding="16px"
          >
            <CsvIcon />
            <Container className="flex flex-column gap-4" width="100%">
              <Text design="GlobalIcoText1" className="H6">
                {leaderboardSettings.upload_filename}
              </Text>
              {leaderboardSettings.upload_filesize && (
                <Text className="C2" design="GlobalIcoText3">
                  {bytesToString(leaderboardSettings.upload_filesize)}
                </Text>
              )}
            </Container>
            <Popover
              closeOnClick
              width="120px"
              height="fit-content"
              renderTriggerButton={({ open }) => (
                <Button
                  className={`flex fd-row jc-center ai-center radius-full padding-0`}
                  width="32px"
                  height="32px"
                  design={TButtonAndLinkDesigns.LocalBttnSecTransp}
                  fixedState={open ? 'hover' : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <RotatableIcon rotate={'90deg'} size={14} name={ICONS.THREE_DOTS_HORIZONTAL} />
                </Button>
              )}
            >
              {() => {
                return (
                  <Container
                    design="GlobalCont2"
                    borderRadius="12px"
                    style={{
                      zIndex: 1001,
                      boxShadow: 'box-shadow: 0 13px 30px 0 rgba(0, 0, 0, 0.25)',
                    }}
                  >
                    <Button
                      hoverBackgroundColor="var(--gray-650)"
                      outlineOnFocus={false}
                      className="radius-0 text-left padding-v-10 padding-h-16 width-100-percent flex ai-center gap-10 alter-theme"
                      onClick={openModal}
                    >
                      <Icon name={ICONS.EDIT} size={12} />
                      <Text color="var(--gray-100)" className="P2">
                        Edit List
                      </Text>
                    </Button>
                    <Button
                      hoverBackgroundColor="var(--gray-650)"
                      outlineOnFocus={false}
                      style={{
                        color: 'var(--global-error-400)',
                      }}
                      className="radius-0 text-left padding-v-10 padding-h-16 width-100-percent flex ai-center gap-10 alter-theme"
                      onClick={handleDeleteCsv}
                    >
                      <Icon name={ICONS.TRASH} size={12} />
                      <Text color="var(--global-error-400)" className="P2">
                        Delete
                      </Text>
                    </Button>
                  </Container>
                );
              }}
            </Popover>
          </Container>
        )}
      </Container>

      <LeaderboardCsvModal
        open={csvModalOpen}
        close={closeModal}
        submit={handleSubmit}
        currentSettings={leaderboardSettings}
      />
      <Modal
        open={deleteConfirmation}
        onRequestClose={closeDeleteConfirmation}
        RenderHeaderContent={() => (
          <Container padding="8px 16px">
            <Text tag="h4" className="h4">
              Delete Confirmation
            </Text>
          </Container>
        )}
        RenderFooterContent={() => (
          <Container padding="8px 16px">
            <div className="flex ai-center jc-flex-end gap-8">
              <Button onClick={closeDeleteConfirmation} focusBackgroundColor="var(--gray-700)">
                <Text>Cancel</Text>
              </Button>
              <Button
                width="94px"
                height="32px"
                onClick={handleDeleteCsv}
                disabled={loading}
                className="flex ai-center jc-center"
                backgroundColor={getThemeColorCSSVariable()}
                loading={loading}
              >
                <Text color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}>
                  Delete
                </Text>
              </Button>
            </div>
          </Container>
        )}
      >
        <Container padding="16px">
          <Text tag="p">
            Are you sure you want to delete the uploaded custom points for this event? This action cannot be undone.
          </Text>
        </Container>
      </Modal>
    </Container>
  );
};

export default LeaderboardCSVUpload;
